import { Injectable, inject, signal } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import { HttpClientService } from '../../../shared/services/http-client/http-client.service';
import {
  TermoDetalheDto,
  TermoFuncionalidadesResponseDto,
  TermoListaItemDto,
  TermoListaResponseDto,
} from '../models/termo-api.model';
import {
  Termo,
  TermoArquivoUpload,
  TermoEdicao,
  TermoFuncionalidadeVinculo,
  TermoFiltros,
  TermoNovo,
  TermoPagina,
  TermoResumo,
  TermoStatus,
} from '../models/termo.model';

const TERMOS_PATH = '/v1/termos';

/** Chamadas à API de Gestão de Termos, convertidas para o modelo da tela. */
@Injectable({ providedIn: 'root' })
export class TermoService {
  private readonly httpClient = inject(HttpClientService);

  /**
   * Termos já abertos. Fornece as funcionalidades que as respostas de gravação
   * não trazem e serve de base para detectar o que mudou na alteração.
   */
  private readonly termos = signal<readonly Termo[]>([]);

  /** GET /v1/termos */
  getTermos(filtros: TermoFiltros, pagina: number, tamanhoPagina: number): Observable<TermoPagina> {
    return this.httpClient
      .get<TermoListaResponseDto>(TERMOS_PATH, {
        params: { ...this.toQueryParams(filtros), pagina, tamanhoPagina },
      })
      .pipe(
        map((resposta) => ({
          itens: (resposta?.itens ?? []).map(mapTermoResumo),
          pagina: resposta?.pagina ?? pagina,
          tamanhoPagina: resposta?.tamanhoPagina ?? tamanhoPagina,
          totalItens: resposta?.totalItens ?? 0,
          totalPaginas: resposta?.totalPaginas ?? 0,
        })),
      );
  }

  /** GET /v1/termos/:id e /v1/termos/:id/funcionalidades, em paralelo. */
  getTermo(id: number): Observable<Termo> {
    return forkJoin({
      dto: this.httpClient.get<TermoDetalheDto>(`${TERMOS_PATH}/${id}`),
      funcionalidades: this.getFuncionalidades(id),
    }).pipe(map(({ dto, funcionalidades }) => this.guardarDaApi(dto, funcionalidades)));
  }

  /** GET /v1/termos/:id/funcionalidades */
  getFuncionalidades(id: number): Observable<TermoFuncionalidadeVinculo[]> {
    return this.httpClient
      .get<TermoFuncionalidadesResponseDto>(`${TERMOS_PATH}/${id}/funcionalidades`)
      .pipe(map(mapFuncionalidades));
  }

  /** Só o status mudou: PATCH /status. Qualquer outra mudança: PUT /v1/termos/:id. */
  atualizarTermo(id: number, edicao: TermoEdicao): Observable<Termo> {
    const atual = this.termos().find((termo) => termo.id === id);
    const soMudouStatus =
      !!atual &&
      edicao.status !== atual.status &&
      edicao.arquivo === null &&
      edicao.nome === atual.nome &&
      edicao.descricao === atual.descricao;

    if (soMudouStatus) {
      return this.alterarStatus(id, edicao.status, edicao.motivoAlteracao);
    }

    return this.httpClient
      .put<TermoDetalheDto>(
        `${TERMOS_PATH}/${id}`,
        toFormData(
          {
            nome: edicao.nome,
            descricao: edicao.descricao,
            status: edicao.status,
            motivo: edicao.motivoAlteracao,
          },
          edicao.arquivo,
        ),
      )
      .pipe(map((dto) => this.guardarDaApi(dto)));
  }

  /** PATCH /v1/termos/:id/status */
  alterarStatus(id: number, status: TermoStatus, motivo: string): Observable<Termo> {
    return this.httpClient
      .patch<TermoDetalheDto>(`${TERMOS_PATH}/${id}/status`, {
        // O endpoint espera "ativo" / "inativo".
        status: status.toLowerCase(),
        motivo: motivo.trim(),
      })
      .pipe(map((dto) => this.guardarDaApi(dto)));
  }

  /** GET /v1/termos/:id/versoes/:idVersao/arquivo */
  getArquivo(idTermo: number, idVersao: number): Observable<Blob> {
    return this.httpClient
      .getBlob(`${TERMOS_PATH}/${idTermo}/versoes/${idVersao}/arquivo`)
      .pipe(
        // Força PDF: com tipo genérico o navegador baixaria em vez de exibir no modal.
        map((arquivo) => new Blob([arquivo], { type: 'application/pdf' })),
      );
  }

  /** PUT /v1/termos/:id/funcionalidades. Ids fora da lista são desvinculados. */
  atualizarVinculos(id: number, idsVinculados: readonly number[]): Observable<Termo> {
    return this.httpClient
      .put<TermoFuncionalidadesResponseDto>(`${TERMOS_PATH}/${id}/funcionalidades`, {
        idsFuncionalidades: [...idsVinculados],
      })
      .pipe(
        map((resposta) => {
          const atual = this.termos().find((termo) => termo.id === id) ?? termoEmBranco(id);
          const termo: Termo = { ...atual, funcionalidades: mapFuncionalidades(resposta) };

          this.termos.update((termos) => [...termos.filter((item) => item.id !== id), termo]);

          return termo;
        }),
      );
  }

  /** POST /v1/termos */
  criarTermo(novo: TermoNovo): Observable<Termo> {
    return this.httpClient
      .post<TermoDetalheDto>(
        TERMOS_PATH,
        toFormData({ nome: novo.nome, descricao: novo.descricao, motivo: novo.motivo }, novo.arquivo),
      )
      .pipe(map((dto) => this.guardarDaApi(dto)));
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  /** Sem `funcionalidades` (respostas de gravação), mantém as que o termo já tinha. */
  private guardarDaApi(
    dto: TermoDetalheDto,
    funcionalidades?: readonly TermoFuncionalidadeVinculo[],
  ): Termo {
    const local = this.termos().find((termo) => termo.id === dto.id);
    const termo: Termo = {
      ...mapTermoDetalhe(dto),
      funcionalidades: funcionalidades ?? (local ?? termoEmBranco(dto.id)).funcionalidades,
    };

    this.termos.update((termos) => [...termos.filter((item) => item.id !== termo.id), termo]);

    return termo;
  }

  /** Omite filtros vazios. */
  private toQueryParams(filtros: TermoFiltros): Record<string, string> {
    const params: Record<string, string> = {};

    for (const [chave, valor] of Object.entries(filtros)) {
      const texto = typeof valor === 'string' ? valor.trim() : '';
      if (texto) {
        params[chave] = texto;
      }
    }

    return params;
  }
}

/** Corpo multipart. Sem PDF novo, a API mantém o atual. */
function toFormData(
  campos: Record<string, string>,
  arquivo: TermoArquivoUpload | null,
): FormData {
  const corpo = new FormData();

  for (const [campo, valor] of Object.entries(campos)) {
    corpo.append(campo, valor.trim());
  }

  if (arquivo?.conteudo) {
    corpo.append('arquivo', arquivo.conteudo, arquivo.nome);
  }

  return corpo;
}

function mapFuncionalidades(
  resposta: TermoFuncionalidadesResponseDto | null,
): TermoFuncionalidadeVinculo[] {
  return (resposta?.itens ?? []).map((item) => ({
    id: item.id,
    nome: item.nome ?? '',
    vinculado: item.vinculado === true,
  }));
}

function mapTermoResumo(dto: TermoListaItemDto): TermoResumo {
  return {
    id: dto.id,
    nome: dto.nome ?? '',
    status: mapStatus(dto.status),
    versao: dto.versaoAtual ?? '',
    dataVersao: apenasData(dto.dataVersaoAtual),
  };
}

/** Sem funcionalidades: vêm de outro endpoint. */
function mapTermoDetalhe(dto: TermoDetalheDto): Omit<Termo, 'funcionalidades'> {
  const versaoAtual = dto.versaoAtual;
  const arquivo = versaoAtual?.arquivo;

  return {
    id: dto.id,
    nome: dto.nome ?? '',
    descricao: dto.descricao ?? '',
    status: mapStatus(dto.status),
    versao: versaoAtual?.versao ?? '',
    dataVersao: apenasData(versaoAtual?.dataVersao),
    arquivo: arquivo
      ? {
          nome: arquivo.nome,
          tamanhoBytes: arquivo.tamanhoBytes,
          enviadoEm: apenasData(arquivo.enviadoEm),
          enviadoPor: arquivo.enviadoPor ?? '',
          // Usado para baixar o PDF em `getArquivo`.
          idVersao: versaoAtual?.id,
        }
      : null,
    historico: (dto.historicoVersoes ?? []).map((linha) => ({
      versao: linha.versao,
      data: apenasData(linha.dataVersao),
      publicadoPor: linha.publicadoPor ?? '',
      alteracoes: linha.alteracoes ?? '',
    })),
  };
}

/** "2026-10-06T15:32:51" → "2026-10-06". */
function apenasData(valor: string | null | undefined): string {
  return (valor ?? '').slice(0, 10);
}

function mapStatus(status: string | null | undefined): TermoStatus {
  return status?.trim().toLowerCase() === 'inativo' ? 'Inativo' : 'Ativo';
}

function termoEmBranco(id: number): Termo {
  return {
    id,
    nome: '',
    descricao: '',
    versao: '',
    dataVersao: '',
    status: 'Ativo',
    arquivo: null,
    historico: [],
    funcionalidades: [],
  };
}
